import React, { useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { useUserStore } from '../../stores/userStore';

export function SettingsScreen() {
  const setScreen = useGameStore(s => s.setScreen);
  const user = useUserStore(s => s.user);
  const updateSettings = useUserStore(s => s.updateSettings);
  const settings = user?.settings;
  const [activeTab, setActiveTab] = useState('graphics');

  if (!settings) return null;

  const tabs = [
    { id: 'graphics', label: 'Graphics', icon: '🖥' },
    { id: 'audio', label: 'Audio', icon: '🔊' },
    { id: 'controls', label: 'Controls', icon: '🎮' },
    { id: 'gameplay', label: 'Gameplay', icon: '🎯' },
    { id: 'accessibility', label: 'Accessibility', icon: '♿' },
  ];

  return (
    <div className="w-full h-full flex flex-col bg-cf-black">
      <div className="flex items-center justify-between px-6 py-4 border-b border-cf-border">
        <h2 className="text-xl font-bold text-cf-white">Settings</h2>
        <button
          onClick={() => setScreen('menu')}
          className="cf-btn-secondary text-xs"
        >
          Back to Menu
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-56 border-r border-cf-border p-3 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-cf-cyan/10 text-cf-cyan border border-cf-cyan/20'
                  : 'text-cf-muted hover:text-cf-text hover:bg-cf-dark'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl space-y-6">
            {activeTab === 'graphics' && (
              <>
                <SettingSlider
                  label="Field of View"
                  value={settings.graphics.fov}
                  min={60} max={120} step={1}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, fov: v } })}
                  suffix="°"
                />
                <SettingSlider
                  label="Render Scale"
                  value={settings.graphics.renderScale}
                  min={0.5} max={1.5} step={0.1}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, renderScale: v } })}
                  suffix="x"
                />
                <SettingSelect
                  label="Quality"
                  value={settings.graphics.quality}
                  options={['low', 'medium', 'high', 'ultra']}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, quality: v as 'low' | 'medium' | 'high' | 'ultra' } })}
                />
                <SettingToggle
                  label="Shadows"
                  checked={settings.graphics.shadows}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, shadows: v } })}
                />
                <SettingToggle
                  label="Anti-Aliasing"
                  checked={settings.graphics.antiAliasing}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, antiAliasing: v } })}
                />
                <SettingToggle
                  label="VSync"
                  checked={settings.graphics.vsync}
                  onChange={(v) => updateSettings({ graphics: { ...settings.graphics, vsync: v } })}
                />
                <SettingToggle
                  label="Fullscreen"
                  checked={settings.graphics.fullscreen}
                  onChange={(v) => {
                    updateSettings({ graphics: { ...settings.graphics, fullscreen: v } });
                    if (v) document.documentElement.requestFullscreen?.();
                    else document.exitFullscreen?.();
                  }}
                />
              </>
            )}

            {activeTab === 'audio' && (
              <>
                <SettingSlider
                  label="Master Volume"
                  value={settings.audio.master}
                  min={0} max={1} step={0.05}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, master: v } })}
                  displayValue={`${Math.round(settings.audio.master * 100)}%`}
                />
                <SettingSlider
                  label="Music"
                  value={settings.audio.music}
                  min={0} max={1} step={0.05}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, music: v } })}
                  displayValue={`${Math.round(settings.audio.music * 100)}%`}
                />
                <SettingSlider
                  label="SFX"
                  value={settings.audio.sfx}
                  min={0} max={1} step={0.05}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, sfx: v } })}
                  displayValue={`${Math.round(settings.audio.sfx * 100)}%`}
                />
                <SettingSlider
                  label="Voice"
                  value={settings.audio.voice}
                  min={0} max={1} step={0.05}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, voice: v } })}
                  displayValue={`${Math.round(settings.audio.voice * 100)}%`}
                />
                <SettingSlider
                  label="Ambient"
                  value={settings.audio.ambient}
                  min={0} max={1} step={0.05}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, ambient: v } })}
                  displayValue={`${Math.round(settings.audio.ambient * 100)}%`}
                />
                <SettingToggle
                  label="Spatial Audio"
                  checked={settings.audio.spatialAudio}
                  onChange={(v) => updateSettings({ audio: { ...settings.audio, spatialAudio: v } })}
                />
              </>
            )}

            {activeTab === 'controls' && (
              <>
                <SettingSlider
                  label="Mouse Sensitivity"
                  value={settings.controls.mouseSensitivity}
                  min={0.5} max={10} step={0.1}
                  onChange={(v) => updateSettings({ controls: { ...settings.controls, mouseSensitivity: v } })}
                  suffix=""
                />
                <SettingSlider
                  label="ADS Sensitivity"
                  value={settings.controls.adsSensitivity}
                  min={0.1} max={3} step={0.1}
                  onChange={(v) => updateSettings({ controls: { ...settings.controls, adsSensitivity: v } })}
                  suffix=""
                />
                <SettingToggle
                  label="Invert Y-Axis"
                  checked={settings.controls.invertY}
                  onChange={(v) => updateSettings({ controls: { ...settings.controls, invertY: v } })}
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-cf-text">Crosshair Color</label>
                  <div className="flex gap-2">
                    {['#00e5ff', '#ff1744', '#00e676', '#ffc400', '#ffffff'].map((color) => (
                      <button
                        key={color}
                        onClick={() => updateSettings({ controls: { ...settings.controls, crosshairColor: color } })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          settings.controls.crosshairColor === color ? 'border-cf-white scale-110' : 'border-cf-border'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'gameplay' && (
              <>
                <SettingToggle
                  label="Auto Reload"
                  checked={settings.gameplay.autoReload}
                  onChange={(v) => updateSettings({ gameplay: { ...settings.gameplay, autoReload: v } })}
                />
                <SettingToggle
                  label="Auto Switch Weapon"
                  checked={settings.gameplay.autoSwitchWeapon}
                  onChange={(v) => updateSettings({ gameplay: { ...settings.gameplay, autoSwitchWeapon: v } })}
                />
                <SettingToggle
                  label="Show Damage Numbers"
                  checked={settings.gameplay.showDamageNumbers}
                  onChange={(v) => updateSettings({ gameplay: { ...settings.gameplay, showDamageNumbers: v } })}
                />
                <SettingToggle
                  label="Show FPS"
                  checked={settings.gameplay.showFPS}
                  onChange={(v) => updateSettings({ gameplay: { ...settings.gameplay, showFPS: v } })}
                />
                <SettingToggle
                  label="Show Ping"
                  checked={settings.gameplay.showPing}
                  onChange={(v) => updateSettings({ gameplay: { ...settings.gameplay, showPing: v } })}
                />
              </>
            )}

            {activeTab === 'accessibility' && (
              <>
                <SettingSelect
                  label="Color Blind Mode"
                  value={settings.accessibility.colorBlindMode}
                  options={['none', 'protanopia', 'deuteranopia', 'tritanopia']}
                  onChange={(v) => updateSettings({ accessibility: { ...settings.accessibility, colorBlindMode: v } })}
                />
                <SettingToggle
                  label="Subtitles"
                  checked={settings.accessibility.subtitles}
                  onChange={(v) => updateSettings({ accessibility: { ...settings.accessibility, subtitles: v } })}
                />
                <SettingToggle
                  label="Screen Shake"
                  checked={settings.accessibility.screenShake}
                  onChange={(v) => updateSettings({ accessibility: { ...settings.accessibility, screenShake: v } })}
                />
                <SettingToggle
                  label="Motion Blur"
                  checked={settings.accessibility.motionBlur}
                  onChange={(v) => updateSettings({ accessibility: { ...settings.accessibility, motionBlur: v } })}
                />
                <SettingToggle
                  label="High Contrast"
                  checked={settings.accessibility.highContrast}
                  onChange={(v) => updateSettings({ accessibility: { ...settings.accessibility, highContrast: v } })}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingSlider({
  label, value, min, max, step, onChange, suffix = '', displayValue,
}: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; suffix?: string; displayValue?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-cf-text">{label}</label>
        <span className="text-sm text-cf-cyan font-mono">
          {displayValue || `${value}${suffix}`}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-cf-dark rounded-full appearance-none cursor-pointer accent-cf-cyan"
      />
    </div>
  );
}

function SettingToggle({
  label, checked, onChange,
}: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-sm font-medium text-cf-text">{label}</label>
      <button
        onClick={() => onChange(!checked)}
        className={`w-10 h-5 rounded-full transition-all relative ${
          checked ? 'bg-cf-cyan' : 'bg-cf-dark border border-cf-border'
        }`}
      >
        <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${
          checked ? 'left-5.5' : 'left-0.5'
        }`} style={{ left: checked ? '22px' : '2px' }} />
      </button>
    </div>
  );
}

function SettingSelect({
  label, value, options, onChange,
}: {
  label: string; value: string; options: string[]; onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-sm font-medium text-cf-text">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cf-input w-40 text-right capitalize"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-cf-dark">
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}
